/**
 * Module 9: the final check (3 minutes). 12 items, 4 options each, shuffled by the
 * player. Pass mark 80 % (10 of 12). Each topic hint points back to its module.
 * "Answer" is the quiz word here (the 20 % exception of round 3); everywhere else the
 * text says "reply".
 */

import type { Module } from "@/features/training/types";

export const MODULE: Module = {
  id: "quiz",
  title: "Check what you know",
  menuTitle: "Final check",
  minutes: 3,
  kind: "quiz",
  goals: ["Answer 12 questions. You pass with 10 right answers (80 %)."],
  slides: [
    {
      id: "quiz",
      tone: "light",
      title: "Check what you know",
      job: "Answer 12 questions. You need 10 right to pass. You can try again as often as you like.",
      interaction: {
        id: "quiz-final",
        kind: "quiz",
        required: true,
        questions: [
          {
            id: "qz-shadow",
            topic: "shadow",
            stem: "You click `Copy the reply`. What has I2Q sent to the customer?",
            options: [
              {
                id: "qz-shadow-a",
                text: "Nothing. You paste it into Outlook and send it yourself.",
                correct: true,
                fb: "Right. I2Q never sends. You send from Outlook.",
              },
              {
                id: "qz-shadow-b",
                text: "The reply, from the shared mailbox.",
                correct: false,
                fb: "No. I2Q runs in shadow mode. Nothing is sent.",
              },
              {
                id: "qz-shadow-c",
                text: "A draft in your Outlook drafts.",
                correct: false,
                fb: "No. The text is only on your clipboard. Outlook shows it after you paste it.",
              },
              {
                id: "qz-shadow-d",
                text: "The quotation, to F&O.",
                correct: false,
                fb: "No. I2Q writes nothing to F&O.",
              },
            ],
          },
          {
            id: "qz-queue",
            topic: "queue",
            stem: "Your queue shows 4 cases. The sidebar says `15 new · 50 open`. Why?",
            options: [
              {
                id: "qz-queue-a",
                text: "46 cases were closed today.",
                correct: false,
                fb: "No. The second number counts open cases, not closed ones.",
              },
              {
                id: "qz-queue-b",
                text: "Colleagues hid them.",
                correct: false,
                fb: "No. Nobody can hide cases from you. The default view shows only some of them.",
              },
              {
                id: "qz-queue-c",
                text: "The default view shows today, yours and nobody's. Use the chip `open, all dates`.",
                correct: true,
                fb: "Right. The rest of your backlog is behind the backlog chips, also `open older than 7 days`.",
              },
              {
                id: "qz-queue-d",
                text: "The count includes parked purchase orders.",
                correct: false,
                fb: "No. Parked purchase orders are not in the open count.",
              },
            ],
          },
          {
            id: "qz-prices",
            topic: "prices",
            stem: "The big button says `Check: Price approval needed`. What next?",
            options: [
              {
                id: "qz-prices-a",
                text: "Send the quotation anyway.",
                correct: false,
                fb: "No. The price is below the limit. It needs an approval.",
              },
              {
                id: "qz-prices-b",
                text: "Decide the rows. Your manager opens the checks under `Details` and clicks `Approve price`.",
                correct: true,
                fb: "Right. `Approve price` appears for your manager in the checks once the rows are decided.",
              },
              {
                id: "qz-prices-c",
                text: "Mark it Not for us.",
                correct: false,
                fb: "No. The inquiry is real. Only the price needs an approval.",
              },
              {
                id: "qz-prices-d",
                text: "Give feedback and wait.",
                correct: false,
                fb: "No. Feedback goes to the build team, not to your manager.",
              },
            ],
          },
          {
            id: "qz-guess",
            topic: "customer",
            stem: "The customer line says Confirm Sample Castings GmbH, Kassel. The mail came from Denmark. What do you do?",
            options: [
              {
                id: "qz-guess-a",
                text: "Click Confirm. The name fits.",
                correct: false,
                fb: "No. A guess in another country with no evidence is likely wrong.",
              },
              {
                id: "qz-guess-b",
                text: "Confirm it and fix it later with feedback.",
                correct: false,
                fb: "No. Feedback does not change the case. A wrong confirmation teaches I2Q the wrong thing.",
              },
              {
                id: "qz-guess-c",
                text: "Set it to Not for us.",
                correct: false,
                fb: "No. The inquiry is real. Only the customer is unclear.",
              },
              {
                id: "qz-guess-d",
                text: "Read the mail and click `Why`. Click `Not them` and search if there is no evidence.",
                correct: true,
                fb: "Right. Confirm only with evidence.",
              },
            ],
          },
          {
            id: "qz-noprice",
            topic: "customer",
            stem: "No line shows a price. What do you check first?",
            options: [
              {
                id: "qz-noprice-a",
                text: "Whether the customer is confirmed.",
                correct: true,
                fb: "Right. Prices follow once you confirm the customer. This is by design.",
              },
              {
                id: "qz-noprice-b",
                text: "Report a bug.",
                correct: false,
                fb: "No. Missing prices before the customer is confirmed are by design.",
              },
              {
                id: "qz-noprice-c",
                text: "Open the `Details` sheet and look at the quotation.",
                correct: false,
                fb: "No. Without a confirmed customer there is no price to show anywhere.",
              },
              {
                id: "qz-noprice-d",
                text: "Reopen the case.",
                correct: false,
                fb: "No. The case is open. The customer is the missing step.",
              },
            ],
          },
          {
            id: "qz-qty",
            topic: "lines",
            stem: "The mail says M12 x 35 bolts, 200 pcs. The row says 35 pcs. What do you do?",
            options: [
              {
                id: "qz-qty-a",
                text: "Press the big button.",
                correct: false,
                fb: "No. The item may be right, but the quantity is wrong.",
              },
              {
                id: "qz-qty-b",
                text: "Give feedback only.",
                correct: false,
                fb: "No. Feedback does not change the row.",
              },
              {
                id: "qz-qty-c",
                text: "Open the row's `Details`, click `Correct` next to the quantity and enter 200.",
                correct: true,
                fb: "Right. 35 is the length of the bolt. The customer wants 200 pcs.",
              },
              {
                id: "qz-qty-d",
                text: "Leave it. The bolt is right.",
                correct: false,
                fb: "No. The right item at the wrong quantity is still a wrong quote.",
              },
            ],
          },
          {
            id: "qz-number",
            topic: "lines",
            stem: "When is Item found by its number enough?",
            options: [
              {
                id: "qz-number-a",
                text: "Always.",
                correct: false,
                fb: "No. On a model number or a line with no number it is a guess.",
              },
              {
                id: "qz-number-b",
                text: "After you compare the row's `Details` with the customer's own words.",
                correct: true,
                fb: "Right. Compare the pick with what the customer wrote.",
              },
              {
                id: "qz-number-c",
                text: "When the row says matched.",
                correct: false,
                fb: "No. The word says how sure I2Q is. It does not say whether the customer meant this item.",
              },
              {
                id: "qz-number-d",
                text: "When the price shows.",
                correct: false,
                fb: "No. A price shows for any item, also for the wrong one.",
              },
            ],
          },
          {
            id: "qz-attached",
            topic: "reply",
            stem: "The reply says: please find attached our quotation. What do you do?",
            options: [
              {
                id: "qz-attached-a",
                text: "Attach a PDF.",
                correct: false,
                fb: "No. The lines belong in the mail text. Remove the sentence instead.",
              },
              {
                id: "qz-attached-b",
                text: "Send it as it is.",
                correct: false,
                fb: "No. The customer would look for an attachment that is not there.",
              },
              {
                id: "qz-attached-c",
                text: "Prepare it again until it changes.",
                correct: false,
                fb: "No. Fix the sentence yourself with `Edit text`.",
              },
              {
                id: "qz-attached-d",
                text: "Open `More`, click `Edit text` and remove it. The lines go into the mail.",
                correct: true,
                fb: "Right. Remove the sentence and keep the lines in the mail.",
              },
            ],
          },
          {
            id: "qz-send",
            topic: "reply",
            stem: "Before you send from Outlook, you check…",
            options: [
              {
                id: "qz-send-a",
                text: "lines, quantities, unit prices, lead time and open points",
                correct: true,
                fb: "Right. You send it, so you check what it promises.",
              },
              {
                id: "qz-send-b",
                text: "only the subject",
                correct: false,
                fb: "No. I2Q copies the subject for you. The mistakes hide in the lines.",
              },
              {
                id: "qz-send-c",
                text: "nothing, I2Q checked it",
                correct: false,
                fb: "No. The reply is a starting text, not a finished mail.",
              },
              {
                id: "qz-send-d",
                text: "only the greeting",
                correct: false,
                fb: "No. A friendly greeting does not correct a wrong quantity.",
              },
            ],
          },
          {
            id: "qz-closed",
            topic: "status",
            stem: "Closed: Moved to the done folder of the shared mailbox. Did the customer get a reply?",
            options: [
              {
                id: "qz-closed-a",
                text: "Yes, always.",
                correct: false,
                fb: "No. Somebody moved the mail. That is not a reply.",
              },
              {
                id: "qz-closed-b",
                text: "Yes, I2Q replied.",
                correct: false,
                fb: "No. I2Q never sends a reply.",
              },
              {
                id: "qz-closed-c",
                text: "Not always. Check the thread. Reopen the case if it still needs a reply.",
                correct: true,
                fb: "Right. Closed is filed in I2Q only. Check the thread.",
              },
              {
                id: "qz-closed-d",
                text: "Somebody deleted it.",
                correct: false,
                fb: "No. Somebody moved the mail, not deleted it. The case is still in the Closed tab.",
              },
            ],
          },
          {
            id: "qz-take",
            topic: "status",
            stem: "You start on a case a colleague may also see. What do you do first?",
            options: [
              {
                id: "qz-take-a",
                text: "Mark it done.",
                correct: false,
                fb: "No. You have not replied yet.",
              },
              {
                id: "qz-take-b",
                text: "Forward it.",
                correct: false,
                fb: "No. A forward creates another copy of the same inquiry.",
              },
              {
                id: "qz-take-c",
                text: "Copy the reply from the queue.",
                correct: false,
                fb: "No. The queue has no reply. Open the case; your first click takes it.",
              },
              {
                id: "qz-take-d",
                text: "Open it and start: the first click takes it, or click Take under More.",
                correct: true,
                fb: "Right. The case is yours from the first click, and colleagues see it.",
              },
            ],
          },
          {
            id: "qz-feedback",
            topic: "feedback",
            stem: "Line 2 has the wrong item. You click Was this right? Wrong. What changes on the case?",
            options: [
              {
                id: "qz-feedback-a",
                text: "Nothing. Use `Pick item` or `No item` to change the case.",
                correct: true,
                fb: "Right. Feedback tells the build team. It does not change the case.",
              },
              {
                id: "qz-feedback-b",
                text: "The item is replaced.",
                correct: false,
                fb: "No. The row stays as it is until you use Pick item or No item.",
              },
              {
                id: "qz-feedback-c",
                text: "The case closes.",
                correct: false,
                fb: "No. Feedback never changes the status.",
              },
              {
                id: "qz-feedback-d",
                text: "Your manager is told.",
                correct: false,
                fb: "No. Feedback goes to the build team.",
              },
            ],
          },
        ],
        topicHints: {
          shadow: {
            module: "welcome",
            text: "Go back to What I2Q does for you: I2Q prepares, you send from Outlook.",
          },
          queue: {
            module: "queue",
            text: "Go back to Your queue: the default view shows only today.",
          },
          customer: {
            module: "customer",
            text: "Go back to Confirm the customer: only confirm with evidence.",
          },
          lines: {
            module: "lines",
            text: "Go back to Check the draft. Remember: the lines that need you are on top, and a warning triangle on a price means below margin. Compare each row with the customer's own words.",
          },
          prices: {
            module: "prices",
            text: "Go back to Prices: confirm the line, then your manager approves the price.",
          },
          reply: {
            module: "reply",
            text: "Go back to Do the one action: check lines, quantities and lead time before you send.",
          },
          status: {
            module: "status",
            text: "Go back to Take, close and reopen: Closed is not a reply.",
          },
          feedback: {
            module: "extras",
            text: "Go back to Feedback: feedback tells the build team, Pick item changes the case.",
          },
        },
      },
      check: "Missed a topic? The results show which module to look at again.",
    },
  ],
};
